import React, { useState, useMemo } from 'react';
import { Switch, Route, Redirect, NavLink, useLocation } from "react-router-dom";

import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { lighten } from '@material-ui/core/styles/colorManipulator';

import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Button from "@material-ui/core/Button";
import LinearProgress from '@material-ui/core/LinearProgress';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

// Icons
import * as Icons from '@material-ui/icons'

import ClubAvatar from 'app/components/club/ClubAvatar';
import LoginForm from 'app/components/auth/LoginForm';
import WelcomeRedirect from 'app/components/auth/Welcome';
import Page from 'app/components/pages/Page';
import LoginSupport from 'app/components/auth/support/LoginSupport';
import PasswordEmailForm from 'app/components/auth/password/EmailForm';
import PasswordResetForm from 'app/components/auth/password/ResetForm';
import GuestForm from 'app/pages/PublicGuestForm';
import Form from 'app/pages/PublicForm';
import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    appBar: {
        backgroundColor: '#fff',
        height: 80,
        maxHeight: 80,
    },
    toolbar: {
        minHeight: "50px",
        alignItems: "center",
        justifyContent: "space-between",
        display: "flex",
        flexWrap: "nowrap",
        maxWidth: theme.width.container,
        margin: '0 auto',
        width: '100%'
    },
    imgContainer: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    imgLink: {
        lineHeight: 0,
        display: 'block',
        textDecoration: 'none'
    },
    listItem: {
        width: 'auto',
        display: 'inline-block',
        marginLeft: theme.spacing(2),
        verticalAlign: 'middle'
    },
    mainAbsolute: {
        position: 'absolute',
        top: 80,
        left: 0,
        overflowY: 'scroll',
        height: 'calc(100vh - 80px)',
        width: '100%'
    },
    menuItem: {
        width: 250
    },
    emptyBorderPrimary: {
        border: '2px solid',
        borderColor: lighten(theme.palette.secondary.main, 0.62)
    },
    publicContainer: {
        zIndex: 12,
        height: '100%',
    },
    publicItem: {
        padding: theme.spacing(4),

        [theme.breakpoints.down('sm')]: {
            padding: 0
        }
    },
    publicBranding: {
        marginBottom: theme.spacing(2),
        textAlign: 'center'
    },
    paper: {
        padding: theme.spacing(4),
        maxWidth: '680px'
    },
}));

function AdvancedMenuItem(props) {
    const { item, toggleDrawer } = props;

    const Icon = item.icon ? Icons[item.icon] : null;

    return (
        <MenuItem
            dense
            button
            to={`/${item.path}`}
            component={NavLink}
            onClick={toggleDrawer(false)}
            >
            {Icon &&
                <ListItemIcon>
                    <Icon />
                </ListItemIcon>
            }
            <ListItemText primary={item.nav_label} />
        </MenuItem>
    )
}

const MenuIcon = Icons.Menu;
const LoginIcon = Icons.ExitToApp;

export default function PublicLayout(props) {

    const { app } = useApp();
    const classes = useStyles();
    const theme = useTheme();
    let location = useLocation();

    const [ loading, setLoading ] = useState(false);
    const [ drawerOpen, setDrawerOpen ] = useState(false);

    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleLoading = value => {
        setLoading(value)
    }

    const toggleDrawer = (opener) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
          return;
        }

        setDrawerOpen(opener)
    };

    const menuItems = useMemo(() => app.club.pages.map((page, i) => {
        return (
            <ListItem disableGutters className={classes.listItem} key={i}>
                <Button
                    component={NavLink}
                    to={`/${page.path}`}
                    color="secondary"
                    key={i}
                    >
                    {page.nav_label}
                </Button>
            </ListItem>
        )
    }), [app.club.pages]);

    const mobileMenuItems = useMemo(() => app.club.pages.map((page, i) => {
        return <AdvancedMenuItem key={i} item={page} nextItem={app.club.pages[i+1]} toggleDrawer={toggleDrawer}/>
    }), [app.club.pages]);

    const routes = useMemo(() => app.club.pages.map((page, i) => (
        <Route path={`/${page.path}`} key={i}>
            <Page id={page.id} global/>
        </Route>
    )), [app.club.pages]);

    return (
        <>
            <AppBar className={classes.appBar} position="fixed">
                { loading ? (
                    <LinearProgress color="secondary"/>
                ) : (
                    <div className={classes.emptyBorderPrimary}/>
                )}
                <Toolbar className={classes.toolbar}>
                    {mobile ? (
                        <>
                            <div className={classes.imgContainer}>
                                <NavLink to="/" className={classes.imgLink}>
                                    <ClubAvatar club={app.club} size="sm" height={60}/>
                                </NavLink>
                            </div>
                            <div>
                                <MenuIcon
                                    color='secondary'
                                    size='large'
                                    onClick={toggleDrawer(true)}
                                />
                                <SwipeableDrawer
                                    anchor='right'
                                    open={drawerOpen}
                                    onClose={toggleDrawer(false)}
                                    onOpen={toggleDrawer(true)}
                                    >
                                    <MenuList className={classes.menuItem}>
                                        {mobileMenuItems}
                                        <MenuItem
                                            dense
                                            button
                                            to={`/login`}
                                            component={NavLink}
                                            onClick={toggleDrawer(false)}
                                            >
                                            <ListItemIcon>
                                                <LoginIcon />
                                            </ListItemIcon>
                                            <ListItemText primary='Login' />
                                        </MenuItem>
                                    </MenuList>
                                </SwipeableDrawer>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={classes.imgContainer}>
                                <NavLink to="/" className={classes.imgLink}>
                                    <ClubAvatar club={app.club} size="sm" height={60}/>
                                </NavLink>
                            </div>
                            <List>
                                {menuItems}
                                <ListItem disableGutters className={classes.listItem}>
                                    <Button
                                        component={NavLink}
                                        to={"/login"}
                                        color="secondary"
                                        >
                                        Login
                                    </Button>
                                </ListItem>
                            </List>
                        </>
                    )}
                </Toolbar>
            </AppBar>
            <main className={classes.mainAbsolute} style={{ backgroundColor: (location.pathname == '/login' || location.pathname == '/event-guest-form') ? null : '#fff' }}>
                <Switch>
                    {routes}
                    <Route path={'/login'} exact>
                        <Grid container alignItems="center" justify="center" className={classes.publicContainer}>
                            <Grid item className={classes.publicItem}>
                                <Paper className={classes.paper} elevation={4}>
                                    <div className={classes.publicBranding}>
                                        <ClubAvatar club={app.club} />
                                    </div>
                                    <LoginForm
                                        onLoading={handleLoading}
                                        loading={loading}
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Route>
                    <Route path={'/login/welcome'} exact>
                        <Grid container alignItems="center" justify="center" className={classes.publicContainer}>
                            <Grid item className={classes.publicItem}>
                                <Paper className={classes.paper} elevation={4}>
                                    <div className={classes.publicBranding}>
                                        <ClubAvatar club={app.club} />
                                    </div>
                                    <WelcomeRedirect
                                        onLoading={handleLoading}
                                        loading={loading}
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Route>
                    <Route path={'/support/login'} exact>
                        <Grid
                            container
                            alignItems="center"
                            justify="center"
                            className={classes.publicContainer}
                            >
                            <Grid item className={classes.publicItem}>
                                <Paper className={classes.paper} elevation={4}>
                                    <div className={classes.publicBranding}>
                                        <ClubAvatar club={app.club} />
                                    </div>
                                    <LoginSupport />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Route>
                    <Route path={'/password/email'} exact>
                        <Grid
                            container
                            alignItems="center"
                            justify="center"
                            className={classes.publicContainer}
                            >
                            <Grid item className={classes.publicItem}>
                                <Paper className={classes.paper} elevation={4}>
                                    <div className={classes.publicBranding}>
                                        <ClubAvatar club={app.club} />
                                    </div>
                                    <PasswordEmailForm onLoading={handleLoading} loading={loading}/>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Route>
                    <Route path={'/password/reset'} exact>
                        <Grid
                            container
                            alignItems="center"
                            justify="center"
                            className={classes.publicContainer}
                            >
                            <Grid item className={classes.publicItem}>
                                <Paper className={classes.paper} elevation={4}>
                                    <div className={classes.publicBranding}>
                                        <ClubAvatar club={app.club} />
                                    </div>
                                    <PasswordResetForm onLoading={handleLoading} loading={loading}/>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Route>
                    <Route path={'/event-guest-form'}>
                        <GuestForm/>
                    </Route>
                    <Route path={'/form/:hash'} exact>
                        <Form/>
                    </Route>
                    <Redirect exact from="/" to={app.club.pages.length ? `/${app.club.pages[0].path}` : '/login'} />
                    <Redirect to="/" />
                </Switch>
            </main>
        </>
    )
};
